import { useState, type FormEvent } from 'react'
import { ALLERGENS, DIETS, allergenName, type AllergenSlug, type DraftAllergen } from '@shared/types'

/** Which of the three groups to render. */
export type AllergenSection = 'allergies' | 'diets' | 'school'

interface Props {
  /** The child's first name, so the question reads like a question. */
  childName: string
  value: DraftAllergen[]
  onChange: (next: DraftAllergen[]) => void
  /**
   * The groups to show, for callers that split them across screens. Settings
   * puts allergies on their own tab and the other two on another, because an
   * allergy is a medical fact with a severity and a diet is a choice -- one
   * word covering both demotes whichever it fits worse.
   *
   * All three by default, which is onboarding: one step, one pass.
   *
   * The draft is still whole whatever is on screen, so a split caller shares
   * one `value` and one save between its screens.
   */
  show?: AllergenSection[]
}

const same = (a: DraftAllergen, kind: string, slug: string, label: string) =>
  a.kind === kind && a.slug === slug && a.label.trim().toLowerCase() === label.trim().toLowerCase()

/**
 * Allergens and school rules, in one editor shared by onboarding and settings.
 *
 * Two questions, deliberately apart. What the child cannot eat carries a
 * severity, because anaphylaxis and a mild intolerance should not look alike.
 * What the school does not allow carries none: a nut-free classroom is a rule,
 * and it binds a child who eats peanut butter happily at home.
 */
export function AllergenPicker({ childName, value, onChange, show = ['allergies', 'diets', 'school'] }: Props) {
  const [otherChild, setOtherChild] = useState('')
  const [otherSchool, setOtherSchool] = useState('')

  const child = value.filter((a) => a.kind === 'child')
  const school = value.filter((a) => a.kind === 'school')
  const who = childName.trim() || 'they'

  const has = (kind: 'child' | 'school' | 'diet', slug: string) => value.some((a) => same(a, kind, slug, ''))

  const toggle = (kind: 'child' | 'school' | 'diet', slug: AllergenSlug) => {
    if (has(kind, slug)) {
      onChange(value.filter((a) => !same(a, kind, slug, '')))
    } else {
      // A school rule and a diet are both rules: binding, with no severity to pick.
      onChange([...value, { kind, slug, label: '', severity: kind === 'child' ? null : 'rule' }])
    }
  }

  const setSeverity = (slug: string, severity: 'severe' | 'avoid') =>
    onChange(value.map((a) => (same(a, 'child', slug, '') ? { ...a, severity } : a)))

  const addOther = (kind: 'child' | 'school', raw: string) => {
    const label = raw.trim().slice(0, 60)
    if (!label) return
    if (value.some((a) => same(a, kind, 'other', label))) return
    onChange([...value, { kind, slug: 'other', label, severity: kind === 'school' ? 'rule' : null }])
  }

  const remove = (row: DraftAllergen) =>
    onChange(value.filter((a) => !same(a, row.kind, row.slug, row.label)))

  const submitOther = (e: FormEvent, kind: 'child' | 'school') => {
    e.preventDefault()
    if (kind === 'child') {
      addOther('child', otherChild)
      setOtherChild('')
    } else {
      addOther('school', otherSchool)
      setOtherSchool('')
    }
  }

  return (
    <div className="allergens">
      {show.includes('allergies') && (
      <section data-section="allergies">
        <h2 className="h-sm">Does {who} have any food allergies?</h2>
        <p className="muted small">Tick what applies. Leave it all blank if nothing does.</p>

        <p className="muted small">
          <b>Severe</b> means a reaction you carry an auto-injector for. It gets a red warning
          everywhere. <b>Avoid</b> is an intolerance or a preference, and stays quiet.
        </p>

        <div className="allergen-grid">
          {ALLERGENS.map((a) => {
            const on = has('child', a.slug)
            const row = child.find((r) => r.slug === a.slug && !r.label)
            return (
              <div key={a.slug} className={`allergen-row ${on ? 'on' : ''} ${on && row?.severity === null ? 'needs' : ''}`}>
                <label className="allergen-check">
                  <input type="checkbox" checked={on} onChange={() => toggle('child', a.slug)} />
                  <span>{a.label}</span>
                </label>
                {on && row && (
                  <div className="allergen-sev" role="group" aria-label={`How serious is ${a.label}?`}>
                    {row.severity === null && <span className="allergen-ask">Which?</span>}
                    <button
                      type="button"
                      className={`sev ${row.severity === 'severe' ? 'on severe' : ''}`}
                      aria-pressed={row.severity === 'severe'}
                      onClick={() => setSeverity(a.slug, 'severe')}
                    >
                      Severe
                    </button>
                    <button
                      type="button"
                      className={`sev ${row.severity === 'avoid' ? 'on' : ''}`}
                      aria-pressed={row.severity === 'avoid'}
                      onClick={() => setSeverity(a.slug, 'avoid')}
                    >
                      Avoid
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <OtherRows
          rows={child.filter((r) => r.slug === 'other')}
          value={otherChild}
          onValue={setOtherChild}
          onSubmit={(e) => submitOther(e, 'child')}
          onRemove={remove}
          onSeverity={(row, severity) =>
            onChange(value.map((a) => (same(a, 'child', 'other', row.label) ? { ...a, severity } : a)))
          }
          id="other-child"
          placeholder="Kiwi, mustard, red dye…"
          label="Anything else?"
        />
      </section>
      )}


      {show.includes('diets') && (
      <section data-section="diets">
        <h2 className="h-sm">Any dietary restrictions?</h2>
        <p className="muted small">
          Not an allergy, but still a no. Picking one flags every food carrying an ingredient it
          rules out, so vegetarian catches meat, fish and gelatin without you tagging each one.
        </p>
        <div className="allergen-chips">
          {DIETS.map((d) => (
            <button
              key={d.slug}
              type="button"
              className={`btn sm ${has('diet', d.slug) ? 'primary' : ''}`}
              aria-pressed={has('diet', d.slug)}
              onClick={() => toggle('diet', d.slug as AllergenSlug)}
            >
              {has('diet', d.slug) ? '✓ ' : ''}
              {d.label}
            </button>
          ))}
        </div>
        <p className="muted small">
          Halal and kosher are not here on purpose. Both need more than an ingredient list, and a
          button that only checked for pork would be claiming to know something it does not. Put
          them in the box below, in your own words.
        </p>
      </section>
      )}


      {show.includes('school') && (
      <section data-section="school">
        <h2 className="h-sm">Any rules at {who === 'they' ? 'school' : `${who}’s school`}?</h2>
        <p className="muted small">
          A nut-free classroom bans a food for everyone, even the kids who can eat it. Separate from
          allergies, so the board can say which one it is.
        </p>
        <div className="allergen-chips">
          {ALLERGENS.map((a) => (
            <button
              key={a.slug}
              type="button"
              className={`btn sm ${has('school', a.slug) ? 'primary' : ''}`}
              aria-pressed={has('school', a.slug)}
              onClick={() => toggle('school', a.slug)}
            >
              {has('school', a.slug) ? '✓ ' : ''}
              No {a.label.toLowerCase()}
            </button>
          ))}
        </div>

        <OtherRows
          rows={school.filter((r) => r.slug === 'other')}
          value={otherSchool}
          onValue={setOtherSchool}
          onSubmit={(e) => submitOther(e, 'school')}
          onRemove={remove}
          id="other-school"
          placeholder="No hot food, no fizzy drinks…"
          label="Anything else?"
        />
      </section>
      )}


      <p className="muted small allergen-caveat">
        Five Lunches flags what you tell it. It cannot read a label or know what a brand changed, so
        check the packet every time.
      </p>
    </div>
  )
}

function OtherRows({
  rows,
  value,
  onValue,
  onSubmit,
  onRemove,
  onSeverity,
  id,
  placeholder,
  label,
}: {
  rows: DraftAllergen[]
  value: string
  onValue: (v: string) => void
  onSubmit: (e: FormEvent) => void
  onRemove: (row: DraftAllergen) => void
  onSeverity?: (row: DraftAllergen, severity: 'severe' | 'avoid') => void
  id: string
  placeholder: string
  label: string
}) {
  return (
    <>
      {rows.length > 0 && (
        <div className="allergen-chips">
          {rows.map((row) => (
            <span
              key={row.label}
              className={`allergen-tag ${row.severity === 'severe' ? 'severe' : ''} ${
                onSeverity && row.severity === null ? 'needs' : ''
              }`}
            >
              {allergenName(row)}
              {onSeverity && (
                <span className="allergen-sev">
                  <button
                    type="button"
                    className={`sev ${row.severity === 'severe' ? 'on severe' : ''}`}
                    aria-pressed={row.severity === 'severe'}
                    onClick={() => onSeverity(row, 'severe')}
                  >
                    Severe
                  </button>
                  <button
                    type="button"
                    className={`sev ${row.severity === 'avoid' ? 'on' : ''}`}
                    aria-pressed={row.severity === 'avoid'}
                    onClick={() => onSeverity(row, 'avoid')}
                  >
                    Avoid
                  </button>
                </span>
              )}
              <button type="button" className="x" aria-label={`Remove ${allergenName(row)}`} onClick={() => onRemove(row)}>
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      <form className="allergen-add" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor={id}>{label}</label>
          <input
            id={id}
            type="text"
            value={value}
            placeholder={placeholder}
            maxLength={60}
            onChange={(e) => onValue(e.target.value)}
          />
        </div>
        <button className="btn sm" type="submit" disabled={!value.trim()}>
          Add
        </button>
      </form>
    </>
  )
}
